import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { type ApiSchema, api } from "@/modules/api";
import {
  SubscriptionPlanKind,
  type SubscriptionPlanKind as SubscriptionPlanKindType,
} from "./types";
import { BillingSubscriptionObservable } from "./BillingSubscriptionObservable";
import { FeatureAccessObservable } from "./FeatureAccessObservable";
import { makeObservable, computed, action, observable, reaction, runInAction } from "mobx";
import { DateTime } from "luxon";
import { GatedFeature } from "@/store/feature-gates/types";
import { type FeatureGateLevel, PaywallContext } from "@/domains/metrics/tracked-event";
import { isAppleProvider, isStripeProvider } from "@/modules/subscriptions/provider";
import pTimeout from "p-timeout";
import type { CommercialUsageLimitRejection } from "@/modules/commercial-plans";

// TODO: make this configurable from the backend
// https://linear.app/mem-labs/issue/MEM-12522/make-the-free-tier-limit-configurable-on-the-backend
const FREE_LIMIT = 25;
const NON_SKIPPABLE_TRIAL_ASSIGNMENT_REQUEST_TIMEOUT_MS = 5_000;
const NON_SKIPPABLE_TRIAL_COMPLETION_REQUEST_TIMEOUT_MS = 5_000;
const PROACTIVE_PRICING_ASSIGNMENT_REQUEST_TIMEOUT_MS = 5_000;

export type ProactivePricingAssignment = {
  experiment_key: string;
  variant_key: string;
  source: string;
  source_key: string;
  display_price_cents: number;
  display_price: string;
  currency: string;
  interval: string;
};

export type NonSkippableTrialAssignment = ApiSchema["NonSkippableTrialAssignmentResponseSchema"];
export type NonSkippableTrialOnboardingProgress = "unknown" | "pending" | "completed";

export const NON_SKIPPABLE_TRIAL_CLIENT_FALLBACK_SOURCE = "client_fallback";
export const PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE = "client_fallback";

type ProactivePricingAssignmentRequestOptions = {
  trackExposure?: boolean;
};

type ProactivePricingAssignmentResponse = {
  data?: ProactivePricingAssignment;
};

type GetProactivePricingAssignment = (
  url: "/v2/me/paywall/proactive-pricing-assignment",
  options: {
    params?: {
      query: {
        track_exposure: boolean;
      };
    };
  }
) => Promise<ProactivePricingAssignmentResponse>;

export const PROACTIVE_PRICING_CONTROL_ASSIGNMENT: ProactivePricingAssignment = {
  experiment_key: "proactive_pricing_v1",
  variant_key: "price-99",
  source: PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE,
  source_key: "agent-pricing",
  display_price_cents: 9900,
  display_price: "$99/month",
  currency: "usd",
  interval: "month",
};

export const NON_SKIPPABLE_TRIAL_CONTROL_ASSIGNMENT: NonSkippableTrialAssignment = {
  experiment_key: "non_skippable_trial_v1",
  variant_key: "control",
  source: NON_SKIPPABLE_TRIAL_CLIENT_FALLBACK_SOURCE,
  source_key: "non-skippable-trial",
  can_skip: true,
};

const parseValidDateTime = (value: string | null | undefined): DateTime | undefined => {
  if (!value) {
    return undefined;
  }

  const dateTime = DateTime.fromISO(value);
  return dateTime.isValid ? dateTime : undefined;
};

const accessEndOutlastsBillingEnd = ({
  accessEndsAt,
  billingEndsAt,
}: {
  accessEndsAt: string | null | undefined;
  billingEndsAt: string | null | undefined;
}): boolean => {
  const billingEnd = parseValidDateTime(billingEndsAt);
  if (!billingEnd) {
    return false;
  }

  if (!accessEndsAt) {
    return true;
  }

  const accessEnd = parseValidDateTime(accessEndsAt);
  return accessEnd !== undefined && accessEnd > billingEnd;
};

const getScheduledAccessEnd = (accessEndsAt: string | null | undefined): string | undefined => {
  if (!accessEndsAt) {
    return undefined;
  }

  const accessEnd = parseValidDateTime(accessEndsAt);
  if (!accessEnd) {
    return undefined;
  }

  return accessEnd >= DateTime.local().startOf("day") ? accessEndsAt : undefined;
};

export class AppStoreSubscriptionStore extends AppSubStore {
  // Centralized upsell modal state
  upsellModalIsOpen: boolean = false;
  upsellModalFeature?: GatedFeature;
  upsellModalContext?: PaywallContext;
  upsellModalLevel?: FeatureGateLevel;
  commercialUsageLimitRejection: CommercialUsageLimitRejection | null = null;
  proactiveUpsellModalIsOpen: boolean = false;
  nonSkippableTrialAssignment?: NonSkippableTrialAssignment;
  nonSkippableTrialAssignmentIsLoading: boolean = false;
  private nonSkippableTrialAssignmentPromise?: Promise<NonSkippableTrialAssignment>;
  private nonSkippableTrialOnboardingCompletionPromise?: Promise<boolean>;
  proactivePricingAssignment?: ProactivePricingAssignment;
  proactivePricingAssignmentIsLoading: boolean = false;
  proactivePricingAssignmentError?: string;
  private proactivePricingAssignmentPromise?: Promise<ProactivePricingAssignment>;
  private proactivePricingAssignmentPromiseTracksExposure = false;
  proactiveUpsellTitle?: string;
  appleProactiveUpgradeModalIsOpen: boolean = false;
  proactiveCheckoutResetKey: number = 0;
  upgradePlanFlowIsActive: boolean = false;
  upgradePlanFlowCallbackUrl: string | null = null;
  upgradePlanFlowCheckoutReturnIsConfirming: boolean = false;
  memProactiveRedirectModalUrl: string | null = null;
  memProactiveRedirectModalIsPreparing: boolean = false;
  memProactiveRedirectModalIsSetup: boolean = false;
  memProactiveRedirectModalOriginalPlanKind: SubscriptionPlanKindType | undefined = undefined;
  memProactiveRedirectModalOriginalEffectivePlanKind: SubscriptionPlanKindType | undefined =
    undefined;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      this,
      | "hasPaidSubscriptionAccess"
      | "hasProactiveSubscriptionAccess"
      | "shouldCloseActiveUsageLimitUpsell"
      | "shouldCloseNativeChatUsageLimitUpsell"
      | "nonSkippableTrialAssignmentPromise"
      | "nonSkippableTrialOnboardingCompletionPromise"
      | "proactivePricingAssignmentPromise"
      | "proactivePricingAssignmentPromiseTracksExposure"
      | "ensureProactivePricingAssignmentWithOptions"
      | "loadNonSkippableTrialAssignment"
      | "loadProactivePricingAssignment"
      | "persistNonSkippableTrialOnboardingCompletion"
      | "activeRenewingAppleProSubscriptionBlocksProactiveUpgrade"
    >(this, {
      billingSubscription: computed,
      featureAccess: computed,
      checkFreeTierNoteLimit: false,
      billingDisplayPlanKind: computed,
      effectiveDisplayPlanKind: computed,
      hasProactiveAccess: computed,
      hasAgentChannelAccess: computed,
      isProactiveTrialEligible: computed,
      isProactiveUpsellTrialEligible: computed,
      isProactiveTrialEligibilityAuthoritative: computed,
      shouldAllowProactiveStandardPrice: computed,
      nonSkippableTrialOnboardingProgress: computed,
      effectivePlanUsesGrantedAccess: computed,
      proactiveAccessScheduledEnd: computed,
      hasPaidSubscriptionAccess: computed,
      hasProactiveSubscriptionAccess: computed,
      shouldCloseActiveUsageLimitUpsell: computed,
      shouldCloseNativeChatUsageLimitUpsell: computed,
      shouldUseAppleProactiveUpgradeModal: computed,
      activeRenewingAppleProSubscriptionBlocksProactiveUpgrade: computed,
      appleProactiveUpgradeCanSubscribe: computed,
      memProactiveRedirectModalIsActive: computed,
      // Upsell modal state/actions
      upsellModalIsOpen: observable,
      upsellModalFeature: observable,
      upsellModalContext: observable,
      upsellModalLevel: observable,
      commercialUsageLimitRejection: observable,
      proactiveUpsellModalIsOpen: observable,
      nonSkippableTrialAssignment: observable,
      nonSkippableTrialAssignmentIsLoading: observable,
      nonSkippableTrialAssignmentPromise: false,
      nonSkippableTrialOnboardingCompletionPromise: false,
      proactivePricingAssignmentPromise: false,
      proactivePricingAssignmentPromiseTracksExposure: false,
      ensureProactivePricingAssignmentWithOptions: false,
      loadNonSkippableTrialAssignment: false,
      loadProactivePricingAssignment: false,
      persistNonSkippableTrialOnboardingCompletion: false,
      proactivePricingAssignment: observable,
      proactivePricingAssignmentIsLoading: observable,
      proactivePricingAssignmentError: observable,
      proactiveUpsellTitle: observable,
      appleProactiveUpgradeModalIsOpen: observable,
      proactiveCheckoutResetKey: observable,
      upgradePlanFlowIsActive: observable,
      upgradePlanFlowCallbackUrl: observable,
      upgradePlanFlowCheckoutReturnIsConfirming: observable,
      memProactiveRedirectModalUrl: observable,
      memProactiveRedirectModalIsPreparing: observable,
      memProactiveRedirectModalIsSetup: observable,
      memProactiveRedirectModalOriginalPlanKind: observable,
      memProactiveRedirectModalOriginalEffectivePlanKind: observable,
      openUsageLimitUpsell: action,
      openCommercialUsageLimit: action,
      closeUpsell: action,
      openProactiveUpsell: action,
      reopenProactiveUpsellAfterCheckoutReturn: action,
      resetProactiveCheckoutProgress: action,
      startUpgradePlanFlow: action,
      startUpgradePlanFlowCheckoutReturnConfirmation: action,
      finishUpgradePlanFlowCheckoutReturnConfirmation: action,
      finishUpgradePlanFlow: action,
      closeProactiveUpsell: action,
      fetchNonSkippableTrialAssignment: action,
      refreshNonSkippableTrialAssignmentAfterPlatformInfoInvalidation: action,
      ensureNonSkippableTrialAssignment: action,
      completeNonSkippableTrialOnboarding: action,
      fetchProactivePricingAssignment: action,
      ensureProactivePricingAssignment: action,
      preloadProactivePricingAssignment: action,
      clearProactivePricingAssignmentError: action,
      closeAppleProactiveUpgradeModal: action,
      prepareMemProactiveRedirectModal: action,
      openMemProactiveRedirectModal: action,
      closeMemProactiveRedirectModal: action,
    });

    reaction(
      () => this.shouldCloseActiveUsageLimitUpsell,
      shouldCloseUpsell => {
        if (shouldCloseUpsell) {
          this.closeUpsell();
        }
      }
    );

    reaction(
      () =>
        (this.proactiveUpsellModalIsOpen || this.appleProactiveUpgradeModalIsOpen) &&
        this.hasProactiveSubscriptionAccess &&
        !this.upgradePlanFlowIsActive,
      shouldCloseProactiveUpgradeModals => {
        if (shouldCloseProactiveUpgradeModals) {
          this.closeProactiveUpsell();
          this.closeAppleProactiveUpgradeModal();
        }
      }
    );
  }

  get shouldCloseActiveUsageLimitUpsell(): boolean {
    if (!this.upsellModalIsOpen) {
      return false;
    }

    if (this.commercialUsageLimitRejection) {
      return false;
    }

    if (this.upsellModalFeature === GatedFeature.NativeChatMessageSend) {
      return this.shouldCloseNativeChatUsageLimitUpsell;
    }

    return this.hasPaidSubscriptionAccess;
  }

  private get shouldCloseNativeChatUsageLimitUpsell(): boolean {
    const nativeChatGate = this.store.featureGates.checkGate(GatedFeature.NativeChatMessageSend);

    if (this.upsellModalLevel === "warning") {
      return nativeChatGate.warnAt === null || nativeChatGate.used < nativeChatGate.warnAt;
    }

    return nativeChatGate.allowed;
  }

  openUsageLimitUpsell(params: {
    used?: number;
    limit?: number;
    feature: GatedFeature | undefined;
    context?: PaywallContext;
    level?: FeatureGateLevel;
  }) {
    // We don't currently surface used/limit inside the modal UI, but we might in the future
    this.upsellModalFeature = params.feature;
    this.upsellModalContext = params.context;
    this.upsellModalLevel = params.level;
    this.commercialUsageLimitRejection = null;
    this.upsellModalIsOpen = true;
  }

  openCommercialUsageLimit(rejection: CommercialUsageLimitRejection) {
    this.upsellModalFeature = undefined;
    this.upsellModalContext = undefined;
    this.upsellModalLevel = "exceeded";
    this.commercialUsageLimitRejection = rejection;
    this.upsellModalIsOpen = true;
  }

  closeUpsell = () => {
    this.upsellModalIsOpen = false;
    this.upsellModalFeature = undefined;
    this.upsellModalContext = undefined;
    this.upsellModalLevel = undefined;
    this.commercialUsageLimitRejection = null;
  };

  openProactiveUpsell = ({ title }: { title?: string } = {}) => {
    this.closeUpsell();
    this.proactiveUpsellTitle = title;

    if (this.shouldUseAppleProactiveUpgradeModal) {
      this.proactiveUpsellModalIsOpen = false;
      this.appleProactiveUpgradeModalIsOpen = true;
      return;
    }

    this.appleProactiveUpgradeModalIsOpen = false;
    this.proactiveUpsellModalIsOpen = true;
  };

  reopenProactiveUpsellAfterCheckoutReturn = () => {
    this.resetProactiveCheckoutProgress();
    this.openProactiveUpsell();
  };

  /** Signals mounted Proactive checkout surfaces to clear in-progress loading state. */
  resetProactiveCheckoutProgress = () => {
    this.proactiveCheckoutResetKey += 1;
  };

  /**
   * Opens the web-only URL upgrade flow while keeping normal onboarding suppressed.
   *
   * The callback is kept in memory because auth redirects preserve the query
   * string until the logged-in app can bootstrap and own the flow.
   */
  startUpgradePlanFlow = ({ callbackUrl }: { callbackUrl: string | null }) => {
    this.upgradePlanFlowIsActive = true;
    this.upgradePlanFlowCallbackUrl = callbackUrl;
    this.openProactiveUpsell();
  };

  /** Keeps the upgrade CTA loading while Stripe return reconciliation is in flight. */
  startUpgradePlanFlowCheckoutReturnConfirmation = () => {
    this.upgradePlanFlowCheckoutReturnIsConfirming = true;
    this.openProactiveUpsell();
  };

  /** Releases the checkout-return loading state while leaving the modal open for retry. */
  finishUpgradePlanFlowCheckoutReturnConfirmation = () => {
    this.upgradePlanFlowCheckoutReturnIsConfirming = false;
    this.resetProactiveCheckoutProgress();
  };

  /** Releases the URL-owned upgrade lock and closes any active Proactive upgrade modal. */
  finishUpgradePlanFlow = () => {
    this.upgradePlanFlowIsActive = false;
    this.upgradePlanFlowCallbackUrl = null;
    this.upgradePlanFlowCheckoutReturnIsConfirming = false;
    this.closeProactiveUpsell();
    this.closeAppleProactiveUpgradeModal();
  };

  closeProactiveUpsell = () => {
    this.proactiveUpsellModalIsOpen = false;
    this.proactiveUpsellTitle = undefined;
  };

  clearProactivePricingAssignmentError = () => {
    this.proactivePricingAssignmentError = undefined;
  };

  private loadNonSkippableTrialAssignment = async (): Promise<NonSkippableTrialAssignment> => {
    this.nonSkippableTrialAssignmentIsLoading = true;

    try {
      const response = await pTimeout(
        api.get("/v2/me/paywall/non-skippable-trial-assignment", {}),
        { milliseconds: NON_SKIPPABLE_TRIAL_ASSIGNMENT_REQUEST_TIMEOUT_MS }
      );

      if (!response.data) {
        throw new Error("No non-skippable trial assignment returned");
      }

      runInAction(() => {
        this.nonSkippableTrialAssignment = response.data;
        this.nonSkippableTrialAssignmentIsLoading = false;
      });

      return response.data;
    } catch {
      const fallbackAssignment =
        this.nonSkippableTrialAssignment ?? NON_SKIPPABLE_TRIAL_CONTROL_ASSIGNMENT;

      runInAction(() => {
        this.nonSkippableTrialAssignment = fallbackAssignment;
        this.nonSkippableTrialAssignmentIsLoading = false;
      });

      return fallbackAssignment;
    }
  };

  /** Refreshes the assignment while sharing one in-flight request across every caller. */
  fetchNonSkippableTrialAssignment = async (): Promise<NonSkippableTrialAssignment> => {
    if (this.nonSkippableTrialAssignmentPromise) {
      return this.nonSkippableTrialAssignmentPromise;
    }

    const assignmentPromise = this.loadNonSkippableTrialAssignment();
    this.nonSkippableTrialAssignmentPromise = assignmentPromise;

    try {
      return await assignmentPromise;
    } finally {
      if (this.nonSkippableTrialAssignmentPromise === assignmentPromise) {
        this.nonSkippableTrialAssignmentPromise = undefined;
      }
    }
  };

  /**
   * Platform-info invalidations are the live signal that another session changed
   * account-level onboarding state. Refresh a pending assignment so open
   * onboarding modals can dismiss without a full page reload.
   */
  refreshNonSkippableTrialAssignmentAfterPlatformInfoInvalidation = async (): Promise<
    NonSkippableTrialAssignment | undefined
  > => {
    if (
      !this.nonSkippableTrialAssignment ||
      this.nonSkippableTrialOnboardingProgress === "completed"
    ) {
      return this.nonSkippableTrialAssignment;
    }

    return this.fetchNonSkippableTrialAssignment();
  };

  /**
   * Returns the cached non-skippable trial assignment, fetching it only when absent.
   */
  ensureNonSkippableTrialAssignment = async (): Promise<NonSkippableTrialAssignment> => {
    if (this.nonSkippableTrialAssignmentPromise) {
      return this.nonSkippableTrialAssignmentPromise;
    }

    if (this.nonSkippableTrialAssignment) {
      return this.nonSkippableTrialAssignment;
    }

    return this.fetchNonSkippableTrialAssignment();
  };

  /**
   * Persists completion only when the client received an authoritative assignment.
   * A client-side control fallback has no assignment ID and remains locally skippable.
   */
  completeNonSkippableTrialOnboarding = async (): Promise<boolean> => {
    const assignment = this.nonSkippableTrialAssignment;
    const assignmentId = assignment?.assignment_id;

    if (!assignmentId || this.nonSkippableTrialOnboardingProgress === "unknown") {
      return false;
    }

    if (this.nonSkippableTrialOnboardingProgress === "completed") {
      return true;
    }

    if (this.nonSkippableTrialOnboardingCompletionPromise) {
      return this.nonSkippableTrialOnboardingCompletionPromise;
    }

    const completionPromise = this.persistNonSkippableTrialOnboardingCompletion(assignmentId);
    this.nonSkippableTrialOnboardingCompletionPromise = completionPromise;

    try {
      return await completionPromise;
    } finally {
      if (this.nonSkippableTrialOnboardingCompletionPromise === completionPromise) {
        this.nonSkippableTrialOnboardingCompletionPromise = undefined;
      }
    }
  };

  private persistNonSkippableTrialOnboardingCompletion = async (
    assignmentId: string
  ): Promise<boolean> => {
    try {
      const response = await pTimeout(
        api.post("/v2/me/paywall/non-skippable-trial-assignment/complete-onboarding", {
          body: { assignment_id: assignmentId },
        }),
        { milliseconds: NON_SKIPPABLE_TRIAL_COMPLETION_REQUEST_TIMEOUT_MS }
      );

      if (!response.data || response.data.onboarding_completed !== true) {
        return false;
      }

      let completionWasApplied = false;
      runInAction(() => {
        if (this.nonSkippableTrialAssignment?.assignment_id === assignmentId) {
          this.nonSkippableTrialAssignment = response.data;
          completionWasApplied = true;
        }
      });
      return completionWasApplied;
    } catch {
      return false;
    }
  };

  private loadProactivePricingAssignment = async (
    trackExposure: boolean
  ): Promise<ProactivePricingAssignment> => {
    this.proactivePricingAssignmentIsLoading = true;
    this.proactivePricingAssignmentError = undefined;

    try {
      const getProactivePricingAssignment = api.get as unknown as GetProactivePricingAssignment;
      const requestOptions = trackExposure ? {} : { params: { query: { track_exposure: false } } };
      const response = await pTimeout(
        getProactivePricingAssignment(
          "/v2/me/paywall/proactive-pricing-assignment",
          requestOptions
        ),
        { milliseconds: PROACTIVE_PRICING_ASSIGNMENT_REQUEST_TIMEOUT_MS }
      );

      if (!response.data) {
        throw new Error("No Proactive pricing assignment returned");
      }

      runInAction(() => {
        this.proactivePricingAssignment = response.data ?? undefined;
        this.proactivePricingAssignmentIsLoading = false;
      });

      return response.data;
    } catch {
      const fallbackAssignment =
        this.proactivePricingAssignment ?? PROACTIVE_PRICING_CONTROL_ASSIGNMENT;

      runInAction(() => {
        this.proactivePricingAssignment = fallbackAssignment;
        this.proactivePricingAssignmentError = undefined;
        this.proactivePricingAssignmentIsLoading = false;
      });

      return fallbackAssignment;
    }
  };

  /** Refreshes assigned pricing while sharing one in-flight request across every caller. */
  fetchProactivePricingAssignment = async ({
    trackExposure = true,
  }: ProactivePricingAssignmentRequestOptions = {}): Promise<ProactivePricingAssignment> => {
    if (this.proactivePricingAssignmentPromise) {
      const inFlightRequestTracksExposure = this.proactivePricingAssignmentPromiseTracksExposure;
      const assignment = await this.proactivePricingAssignmentPromise;

      if (trackExposure && !inFlightRequestTracksExposure) {
        return this.fetchProactivePricingAssignment({ trackExposure: true });
      }

      return assignment;
    }

    const assignmentPromise = this.loadProactivePricingAssignment(trackExposure);
    this.proactivePricingAssignmentPromise = assignmentPromise;
    this.proactivePricingAssignmentPromiseTracksExposure = trackExposure;

    try {
      return await assignmentPromise;
    } finally {
      if (this.proactivePricingAssignmentPromise === assignmentPromise) {
        this.proactivePricingAssignmentPromise = undefined;
        this.proactivePricingAssignmentPromiseTracksExposure = false;
      }
    }
  };

  /**
   * Returns the cached Proactive pricing assignment, fetching it only when absent.
   */
  ensureProactivePricingAssignment = async (): Promise<ProactivePricingAssignment> => {
    return this.ensureProactivePricingAssignmentWithOptions();
  };

  /** Returns cached pricing or preloads it without counting an experiment exposure. */
  preloadProactivePricingAssignment = async (): Promise<ProactivePricingAssignment> => {
    return this.ensureProactivePricingAssignmentWithOptions({ trackExposure: false });
  };

  private ensureProactivePricingAssignmentWithOptions = async (
    options: ProactivePricingAssignmentRequestOptions = {}
  ): Promise<ProactivePricingAssignment> => {
    if (this.proactivePricingAssignmentPromise) {
      return this.fetchProactivePricingAssignment(options);
    }

    if (this.proactivePricingAssignment) {
      return this.proactivePricingAssignment;
    }

    return this.fetchProactivePricingAssignment(options);
  };

  closeAppleProactiveUpgradeModal = () => {
    this.appleProactiveUpgradeModalIsOpen = false;
    this.proactiveUpsellTitle = undefined;
  };

  get shouldUseAppleProactiveUpgradeModal(): boolean {
    const billingSubscription = this.billingSubscription;

    return (
      billingSubscription !== undefined &&
      isAppleProvider(billingSubscription.provider) &&
      billingSubscription.planKind === SubscriptionPlanKind.PRO &&
      billingSubscription.isActivePaid
    );
  }

  private get activeRenewingAppleProSubscriptionBlocksProactiveUpgrade(): boolean {
    const billingSubscription = this.billingSubscription;

    return (
      billingSubscription !== undefined &&
      isAppleProvider(billingSubscription.provider) &&
      billingSubscription.planKind === SubscriptionPlanKind.PRO &&
      billingSubscription.isActivePaid &&
      !billingSubscription.isEnding
    );
  }

  get appleProactiveUpgradeCanSubscribe(): boolean {
    return !this.activeRenewingAppleProSubscriptionBlocksProactiveUpgrade;
  }

  get memProactiveRedirectModalIsActive(): boolean {
    return this.memProactiveRedirectModalIsPreparing || this.memProactiveRedirectModalUrl !== null;
  }

  prepareMemProactiveRedirectModal = () => {
    this.closeUpsell();
    this.closeProactiveUpsell();
    this.closeAppleProactiveUpgradeModal();

    this.memProactiveRedirectModalIsPreparing = true;
    this.memProactiveRedirectModalUrl = null;
    this.memProactiveRedirectModalIsSetup = false;
    this.memProactiveRedirectModalOriginalPlanKind = undefined;
    this.memProactiveRedirectModalOriginalEffectivePlanKind = undefined;
  };

  openMemProactiveRedirectModal = ({
    redirectUrl,
    isSetup,
    originalPlanKind,
    originalEffectivePlanKind,
  }: {
    redirectUrl: string;
    isSetup: boolean;
    originalPlanKind?: SubscriptionPlanKindType;
    originalEffectivePlanKind?: SubscriptionPlanKindType;
  }) => {
    this.memProactiveRedirectModalUrl = redirectUrl;
    this.memProactiveRedirectModalIsPreparing = false;
    this.memProactiveRedirectModalIsSetup = isSetup;
    this.memProactiveRedirectModalOriginalPlanKind = originalPlanKind;
    this.memProactiveRedirectModalOriginalEffectivePlanKind = originalEffectivePlanKind;
  };

  closeMemProactiveRedirectModal = () => {
    this.memProactiveRedirectModalUrl = null;
    this.memProactiveRedirectModalIsPreparing = false;
    this.memProactiveRedirectModalIsSetup = false;
    this.memProactiveRedirectModalOriginalPlanKind = undefined;
    this.memProactiveRedirectModalOriginalEffectivePlanKind = undefined;
  };

  /**
   * Billing plan kind for account display only.
   *
   * Product gates and access checks should use `featureAccess`, because a user
   * can have a Pro billing subscription and a higher House entitlement.
   */
  public get billingDisplayPlanKind(): SubscriptionPlanKind | undefined {
    return this.billingSubscription?.planKind;
  }

  /**
   * User-visible plan kind after applying effective feature access.
   *
   * Billing still owns checkout and billing-management flows. Display surfaces
   * use this so House grants do not look like Free accounts.
   */
  public get effectiveDisplayPlanKind(): SubscriptionPlanKind | undefined {
    const featureAccess = this.featureAccess;
    if (featureAccess?.isPaid === true) {
      return featureAccess.planKind;
    }

    return this.billingSubscription?.planKind ?? featureAccess?.planKind;
  }

  public get hasProactiveAccess(): boolean {
    if (
      this.store.platformInfo.subscriptionWasCompletedSuccessfully === true &&
      this.store.platformInfo.completedSubscriptionPlanKind === SubscriptionPlanKind.PROACTIVE
    ) {
      return true;
    }

    const featureAccess = this.featureAccess;
    if (featureAccess) {
      return featureAccess.isProactive;
    }

    const billingSubscription = this.billingSubscription;
    return billingSubscription?.isProactive === true && !billingSubscription.hasExpiredEnd;
  }

  /** Every effective paid Mem plan includes Agent delivery channels. */
  public get hasAgentChannelAccess(): boolean {
    return this.hasPaidSubscriptionAccess;
  }

  public get isProactiveTrialEligible(): boolean {
    const billingSubscription = this.billingSubscription;
    const featureAccess = this.featureAccess;
    const hasPaidAccess = featureAccess?.isPaid === true || billingSubscription?.isPaid === true;
    const currentProAccessIsGranted =
      this.effectivePlanUsesGrantedAccess && featureAccess?.isPro === true;
    const serverAllowsStripeTrial =
      this.isProactiveTrialEligibilityAuthoritative &&
      this.store.platformInfo.proactiveTrialEligibility?.stripe_trial_eligible === true;

    return (!hasPaidAccess || currentProAccessIsGranted) && serverAllowsStripeTrial;
  }

  /**
   * Whether the current account can start a Proactive trial from an upsell.
   *
   * Paid Stripe Pro subscriptions can use this offer without becoming eligible
   * for the Free-tier trial-onboarding experiment again.
   */
  public get isProactiveUpsellTrialEligible(): boolean {
    const billingSubscription = this.billingSubscription;
    const hasPaidStripeProSubscription =
      billingSubscription?.isPaid === true &&
      billingSubscription.planKind === SubscriptionPlanKind.PRO &&
      isStripeProvider(billingSubscription.provider);
    const serverAllowsStripeTrial =
      this.isProactiveTrialEligibilityAuthoritative &&
      this.store.platformInfo.proactiveTrialEligibility?.stripe_trial_eligible === true;

    return (
      (this.isProactiveTrialEligible || hasPaidStripeProSubscription) && serverAllowsStripeTrial
    );
  }

  /** Whether trial eligibility came from a successful remote platform-info response. */
  public get isProactiveTrialEligibilityAuthoritative(): boolean {
    return (
      this.store.platformInfo.fetchPlatformInfoState.data === true &&
      this.store.platformInfo.subscription !== null &&
      this.store.platformInfo.entitlements !== null &&
      this.store.platformInfo.proactiveTrialEligibility !== undefined
    );
  }

  /**
   * Whether Proactive checkout may continue at standard price.
   *
   * Require an explicit server denial so a stale or older platform-info
   * response can never opt the user into a paid checkout.
   */
  public get shouldAllowProactiveStandardPrice(): boolean {
    return (
      this.isProactiveTrialEligibilityAuthoritative &&
      this.store.platformInfo.proactiveTrialEligibility?.stripe_trial_eligible === false &&
      !this.hasProactiveAccess
    );
  }

  /** Account-shared onboarding progress, or unknown for client fallback/stale API shapes. */
  public get nonSkippableTrialOnboardingProgress(): NonSkippableTrialOnboardingProgress {
    const assignment = this.nonSkippableTrialAssignment;

    if (!assignment?.assignment_id || typeof assignment.onboarding_completed !== "boolean") {
      return "unknown";
    }

    return assignment.onboarding_completed ? "completed" : "pending";
  }

  public get effectivePlanUsesGrantedAccess(): boolean {
    const featureAccess = this.featureAccess;
    if (featureAccess?.isPaid !== true) {
      return false;
    }

    const billingSubscription = this.billingSubscription;
    if (billingSubscription?.isActivePaid !== true) {
      return true;
    }

    return (
      billingSubscription.planKind !== featureAccess.planKind ||
      billingSubscription.planVariant !== featureAccess.planVariant ||
      (billingSubscription.isScheduledToEnd &&
        accessEndOutlastsBillingEnd({
          accessEndsAt: featureAccess.expiresAt,
          billingEndsAt: billingSubscription.effectiveSubscriptionEndsAt,
        }))
    );
  }

  public get proactiveAccessScheduledEnd(): string | undefined {
    const featureAccess = this.featureAccess;
    const billingSubscription = this.billingSubscription;

    if (featureAccess?.isProactive === true && this.effectivePlanUsesGrantedAccess) {
      return getScheduledAccessEnd(featureAccess.expiresAt);
    }

    if (billingSubscription?.isProactive === true && billingSubscription.isScheduledToEnd) {
      return billingSubscription.effectiveSubscriptionEndsAt ?? undefined;
    }

    return undefined;
  }

  private get hasPaidSubscriptionAccess(): boolean {
    const featureAccess = this.featureAccess;
    const billingSubscription = this.billingSubscription;

    return (
      featureAccess?.isPaid === true ||
      (featureAccess === undefined && billingSubscription?.isActivePaid === true) ||
      this.store.platformInfo.subscriptionWasCompletedSuccessfully === true
    );
  }

  private get hasProactiveSubscriptionAccess(): boolean {
    return this.hasProactiveAccess;
  }

  /**
   * Billing-source subscription data for checkout, billing management, and
   * account display. Use `featureAccess` for product access decisions.
   */
  get billingSubscription(): BillingSubscriptionObservable | undefined {
    const platformInfoSubscription = this.store.platformInfo.subscription;

    if (!platformInfoSubscription) {
      return undefined;
    }

    return new BillingSubscriptionObservable({
      data: platformInfoSubscription,
      store: this.store,
    });
  }

  /**
   * Effective product access from platform entitlements.
   *
   * This is the source product gates should use so House grants can unlock
   * features without changing the user's Stripe or Apple billing plan.
   */
  get featureAccess(): FeatureAccessObservable | undefined {
    const platformInfoEntitlements = this.store.platformInfo.entitlements;

    if (!platformInfoEntitlements) {
      return undefined;
    }

    return new FeatureAccessObservable({
      data: platformInfoEntitlements,
      store: this.store,
    });
  }

  public async checkFreeTierNoteLimit(): Promise<{
    canCreate: boolean;
    used: number;
    limit: number;
  }> {
    const featureAccess = this.featureAccess;

    const termAgo = DateTime.now().minus({ weeks: 1 });
    const notesThisTerm = await this.store.notes.countMyNotesCreatedSince(termAgo);

    if (featureAccess?.isPaid) {
      // Paid users have unlimited notes.
      return { canCreate: true, used: notesThisTerm, limit: Infinity };
    }

    return {
      canCreate: notesThisTerm < FREE_LIMIT,
      used: notesThisTerm,
      limit: FREE_LIMIT,
    };
  }
}
